'use client';

import { useCallback, useMemo, useState } from 'react';

type PermissionTree = {
  handle: string;
  id: string;
  status: number;
  children: PermissionTree[];
};

const usePersonalPermissions = (tree: PermissionTree[]) => {
  const [clearableStack, setClearableStack] = useState<string[]>([]);

  const sources = useMemo(() => {
    const node = tree.find(({ handle }) => handle === 'SOURCE');
    return node ? node.children : [];
  }, [tree]);

  const seeks = useMemo(() => {
    const node = tree.find(({ handle }) => handle === 'SEEK');
    return node ? node.children : [];
  }, [tree]);

  const permissionSources = useCallback(
    (source?: string, seek?: string) => {
      if (!seek && !source) return sources;
      if (source) return sources.filter(({ handle }) => handle === source);
      if (seek === 'ANYONE') return seeks;

      const node = seeks.find(({ handle }) => handle === seek);
      if (node) return node.children.filter(({ status }) => status === 1);

      return [];
    },
    [sources, seeks]
  );

  const permissionSeeks = useCallback(
    (source?: string, seek?: string) => {
      if (!source && !seek) return seeks;
      if (seek) return seeks.filter(({ handle }) => handle === seek);
      if (source === 'ANYONE') return sources;

      const node = sources.find(({ handle }) => handle === source);
      if (node) return node.children.filter(({ status }) => status === 1);

      return [];
    },
    [seeks, sources]
  );

  const isPermissionClearable = useCallback(
    (handle: string) =>
      clearableStack.length === 0
        ? true
        : clearableStack[clearableStack.length - 1] === handle,
    [clearableStack]
  );

  const pushClearablePermission = useCallback(
    (orientation: string) =>
      setClearableStack((prev) => {
        if (prev.length === 0) return prev.concat(orientation);
        if (prev.length < 2 && prev[prev.length - 1] !== orientation)
          return prev.concat(orientation);
        return prev;
      }),
    [setClearableStack]
  );

  const popClearablePermission = useCallback(
    (orientation: string) =>
      setClearableStack((prev) =>
        prev.filter((handle) => handle !== orientation)
      ),
    [setClearableStack]
  );

  return {
    permissionSources,
    permissionSeeks,
    pushClearablePermission,
    popClearablePermission,
    isPermissionClearable,
  };
};

export default usePersonalPermissions;
